<template>
  <div class="grid gap-3 grid-cols-1">
    <button
      v-if="canCreateSchedule"
      data-testid="manage-scale-criar-escala-button"
      class="w-full hover:shadow-lg bg-primary30 text-white text-left px-6 py-6 rounded-lg flex items-center justify-between"
      @click="handleOpenCreateScale"
    >
      <div class="flex flex-col gap-2">
        <div
          class="flex items-center justify-center w-14 h-14 rounded-[11.2px] bg-primary50 shrink-0"
        >
          <GerenciarEscalasIcon width="32" height="32" color="white" />
        </div>
        <h2 class="text-base font-bold leading-6 tracking-[0.08px]">Criar escala</h2>
        <p class="text-sm font-normal leading-[18px] tracking-[0.07px] text-[#F0F0F4]">
          Configure uma nova escala de trabalho
        </p>
      </div>
      <svg class="w-6 h-6 shrink-0" viewBox="0 0 24 24">
        <path :d="mdiChevronRight" fill="white" />
      </svg>
    </button>
  </div>

  <ListSchedules
    @removeSchedule="handleRemove"
    @updateSchedule="handleUpdate"
    @updatePosition="handleUpdatePosition"
    @removePosition="handleRemovePosition"
    @showPositions="handleToShowPositions"
    @showCompanyPolicies="handleToShowCompanyPolicies"
    @showHealthUnitPolicies="handleToShowHealthUnitPolicies"
    @showSchedulePolicies="handleToShowSchedulePolicies"
    @showPositionPolicies="handleToShowPositionPolicies"
  />
  <CreateHospitalModal
    type="create"
    :modalStateProp="showCreateHospitalModal"
    @close="showCreateHospitalModal = false"
  />
  <RemoveScheduleModal :schedule-id="scheduleIdToDelete" :schedule-name="scheduleNameToDelete" />
  <!-- Legado e inacessível: nada liga EModals.UPDATE_SCHEDULE; a edição abre o EditScheduleModal abaixo. Ver o topo de modals/updateSchedule.vue. -->
  <UpdateScheduleModal
    v-if="modals[EModals.UPDATE_SCHEDULE] && scheduleToUpdate"
    :schedule-id="scheduleToUpdate.id"
  />
  <PositionsModal :schedule-id="scheduleIdToShowPositions" />
  <PoliciesModal
    :companie-id="companyIdToShowPolicies"
    :healthUnit-id="healthUnitIdToShowPolicies"
    :schedule-id="scheduleIdToShowPolicies"
    :position-id="positionIdToShowPolicies"
  />

  <RemovePositionModal :schedule-id="scheduleIdToDelete" :position-id="positionIdToDelete" />
  <UpdatePositionModal
    v-if="modals[EModals.UPDATE_POSITION]"
    :schedule-id="scheduleIdToShowPositions"
    :position-id="positionIdToUpdate"
  />

  <template v-if="scheduleToUpdate">
    <EditScheduleModal
      v-show="modals[EModals.EDIT_SCHEDULE]"
      :schedule-details="scheduleToUpdate"
      :initial-tab-slug="initialTabSlug"
    />
  </template>
</template>

<script lang="ts">
import { onMounted, ref, computed, nextTick } from 'vue'
import ListSchedules from './ListSchedules.vue'
import CreateHospitalModal from '@/modules/company/Units/modals/UnitFormModal.vue'
import RemoveScheduleModal from './modals/removeSchedule.vue'
import PositionsModal from './modals/positions.vue'
import PoliciesModal from './modals/policies.vue'
import UpdateScheduleModal from './modals/updateSchedule.vue'
import UpdatePositionModal from '@/components/ManageScale/Schedule/modals/updatePosition.vue'
import RemovePositionModal from '@/components/ManageScale/Schedule/modals/removePosition.vue'
import EditScheduleModal from '@/components/designSystem/modals/EditScheduleModal/index.vue'
import GerenciarEscalasIcon from '@/assets/icons/gerenciar-escalas.vue'
import Network from '@/Network'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useCreateScheduleStore } from '@/pinia-store/useCreateScheduleStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { mdiChevronRight } from '@mdi/js'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { buildScheduleDetails, type ScheduleDetails } from './deepLink'

export default {
  components: {
    ListSchedules,
    RemoveScheduleModal,
    PositionsModal,
    UpdateScheduleModal,
    EditScheduleModal,
    CreateHospitalModal,
    PoliciesModal,
    UpdatePositionModal,
    RemovePositionModal,
    GerenciarEscalasIcon
  },
  setup() {
    const headerStore = useHeaderStore()

    const modalStore = useModalStore()
    const { modals } = storeToRefs(modalStore)
    const healthUnitStore = useHealthUnitStore()
    const scheduleStore = useScheduleStore()
    const permissionsStore = usePermissionsStore()
    const toast = useToast()
    const { notifyError } = useErrorToast()

    const router = useRouter()

    const canCreateSchedule = computed(() => {
      return permissionsStore.checkAnyRessources({
        permission: AuthorizationPermissions.ScheduleCreating,
        resourceType: AuthorizationResourcesAndSubjects.Schedule
      })
    })

    onMounted(async () => {
      headerStore.setTitle(
        'Gerenciar Escalas',
        'Gerencie escalas de trabalho, cargos e políticas associadas'
      )

      // A scheduleStore não toasta mais (padrão canônico): seus loads falhos notificam aqui.
      // A carga de opções de unidade ainda notifica uma vez, dentro da healthUnitStore.
      await Promise.allSettled([
        healthUnitStore.getHealthUnitOptions(true),
        scheduleStore
          .getScheduleOptions(true)
          .catch((error) => notifyError(error, 'Não foi possível carregar as opções de escalas')),
        scheduleStore
          .getSchedulesForPage()
          .catch((error) => notifyError(error, 'Não foi possível carregar as escalas'))
      ])

      const openScheduleId = router.currentRoute.value.query.openScheduleId
      const editTab = router.currentRoute.value.query.editTab
      const scheduleId = Number(openScheduleId)
      if (openScheduleId && !Number.isNaN(scheduleId)) {
        // Limpar a query em TODOS os desfechos (sucesso, sem empresa e erro de
        // rede) — sem isso, um reload re-dispara o deep-link quebrado em loop.
        const query = { ...router.currentRoute.value.query }
        delete query.openScheduleId
        delete query.editTab

        try {
          const schedule = await Network.schedule.getById(scheduleId)
          const scheduleDetails = buildScheduleDetails(schedule)

          if (!scheduleDetails) {
            toast.error(
              'Não foi possível identificar a empresa da escala. Abra a escala pela lista de Gerenciar Escalas.'
            )
            router.replace({ query })
            return
          }

          scheduleToUpdate.value = scheduleDetails
          initialTabSlug.value = typeof editTab === 'string' ? editTab : null
          modalStore.toggleModal(EModals.EDIT_SCHEDULE)
          await nextTick()
          initialTabSlug.value = null
          router.replace({ query })
        } catch (error) {
          console.error('Erro ao buscar escala do deep-link:', error)
          notifyError(error, 'Não foi possível abrir a escala do link.')
          router.replace({ query })
        }
      }
    })

    const showCreateHospitalModal = ref(false)
    const scheduleIdToDelete = ref<number>(0)
    const scheduleNameToDelete = ref('')
    const scheduleIdToShowPositions = ref<number>(0)

    const companyIdToShowPolicies = ref<number | null>(null)
    const healthUnitIdToShowPolicies = ref<number | null>(null)
    const scheduleIdToShowPolicies = ref<number | null>(null)
    const positionIdToShowPolicies = ref<number | null>(null)

    const scheduleToUpdate = ref<ScheduleDetails | null>(null)
    const initialTabSlug = ref<string | null>(null)

    const positionIdToDelete = ref<number>(0)
    const positionIdToUpdate = ref<number>(0)

    const openCreateHospitalModal = () => {
      showCreateHospitalModal.value = true
    }

    const handleRemove = ({ id, name }: { id: number; name: string }) => {
      scheduleIdToDelete.value = id
      scheduleNameToDelete.value = name
    }

    const handleToShowCompanyPolicies = (id: number) => {
      companyIdToShowPolicies.value = id
      healthUnitIdToShowPolicies.value = null
      scheduleIdToShowPolicies.value = null
      positionIdToShowPolicies.value = null
    }

    const handleToShowHealthUnitPolicies = (id: number) => {
      companyIdToShowPolicies.value = null
      healthUnitIdToShowPolicies.value = id
      scheduleIdToShowPolicies.value = null
      positionIdToShowPolicies.value = null
    }

    const handleToShowSchedulePolicies = (id: number) => {
      companyIdToShowPolicies.value = null
      healthUnitIdToShowPolicies.value = null
      scheduleIdToShowPolicies.value = id
      positionIdToShowPolicies.value = null
    }

    const handleToShowPositionPolicies = (id: number) => {
      companyIdToShowPolicies.value = null
      healthUnitIdToShowPolicies.value = null
      scheduleIdToShowPolicies.value = null
      positionIdToShowPolicies.value = id
    }

    const handleToShowPositions = (id: number) => {
      scheduleIdToShowPositions.value = id
    }

    const handleUpdate = (details: ScheduleDetails) => {
      scheduleToUpdate.value = details
    }

    const handleRemovePosition = (id: number) => {
      positionIdToDelete.value = id
    }

    const handleUpdatePosition = ({
      scheduleId,
      positionId
    }: {
      scheduleId: number
      positionId: number
    }) => {
      scheduleIdToShowPositions.value = scheduleId
      positionIdToUpdate.value = positionId
    }

    const createScheduleStore = useCreateScheduleStore()

    const handleOpenCreateScale = () => {
      createScheduleStore.reset()
      router.push({ name: 'criar-escala' })
    }

    return {
      handleOpenCreateScale,
      handleRemove,
      handleUpdate,
      openCreateHospitalModal,
      scheduleIdToDelete,
      scheduleNameToDelete,
      scheduleIdToShowPositions,
      scheduleToUpdate,
      handleToShowPositions,
      showCreateHospitalModal,
      modals,
      EModals,
      PoliciesModal,
      companyIdToShowPolicies,
      healthUnitIdToShowPolicies,
      scheduleIdToShowPolicies,
      positionIdToShowPolicies,
      handleToShowCompanyPolicies,
      handleToShowHealthUnitPolicies,
      handleToShowSchedulePolicies,
      handleToShowPositionPolicies,
      handleRemovePosition,
      handleUpdatePosition,
      UpdatePositionModal,
      RemovePositionModal,
      positionIdToDelete,
      positionIdToUpdate,
      canCreateSchedule,
      initialTabSlug,
      mdiChevronRight
    }
  }
}
</script>

<style></style>
